/* Bowkart Smart Cart Drawer v1 */
(() => {
  class BowkartSmartCart {
    constructor(root) {
      this.root = root;
      this.panel = root.querySelector('.bk-smart-cart__panel');
      this.itemsEl = root.querySelector('[data-bk-cart-items]');
      this.emptyEl = root.querySelector('[data-bk-empty]');
      this.loadingEl = root.querySelector('[data-bk-loading]');
      this.footerEl = root.querySelector('[data-bk-footer]');
      this.countTextEl = root.querySelector('[data-bk-cart-count-text]');
      this.subtotalEl = root.querySelector('[data-bk-subtotal]');
      this.checkoutTotalEl = root.querySelector('[data-bk-checkout-total]');
      this.savingsRow = root.querySelector('[data-bk-savings-row]');
      this.savingsEl = root.querySelector('[data-bk-savings]');
      this.progressEl = root.querySelector('[data-bk-shipping-progress]');
      this.shippingMessageEl = root.querySelector('[data-bk-shipping-message]');
      this.shippingValueEl = root.querySelector('[data-bk-shipping-value]');
      this.crossSellEl = root.querySelector('[data-bk-crosssell]');
      this.recsEl = root.querySelector('[data-bk-recommendations]');
      this.itemTemplate = root.querySelector('[data-bk-item-template]');
      this.recTemplate = root.querySelector('[data-bk-rec-template]');
      this.currency = root.dataset.currency || 'INR';
      this.locale = root.dataset.locale || 'en-IN';
      this.threshold = Number(root.dataset.freeShippingThreshold || 0);
      this.recLimit = Number(root.dataset.recommendationLimit || 3);
      this.recIntent = root.dataset.recommendationIntent || 'related';
      this.recSource = root.dataset.recommendationSource || 'ai';
      this.aiEndpoint = (root.dataset.aiEndpoint || '').trim();
      this.openAfterAdd = root.dataset.openAfterAdd === 'true';
      this.interceptCartLinks = root.dataset.interceptCartLinks === 'true';
      this.config = this.readConfig();
      this.cart = null;
      this.lastFocused = null;
      this.busy = false;
      this.refreshTimer = null;
      this.lastNetworkCartAction = 0;

      this.bind();
      this.patchKallesFriendlyHooks();
      this.installCartNetworkObserver();
    }

    readConfig() {
      try {
        return JSON.parse(this.root.querySelector('[data-bk-config]')?.textContent || '{}');
      } catch (_) {
        return {};
      }
    }

    route(path) {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      return root.replace(/\/$/, '') + path;
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          maximumFractionDigits: 0
        }).format((Number(cents) || 0) / 100);
      } catch (_) {
        return '₹' + Math.round((Number(cents) || 0) / 100).toLocaleString('en-IN');
      }
    }

    bind() {
      this.root.addEventListener('click', (e) => {
        if (e.target.closest('[data-bk-cart-close]')) {
          e.preventDefault();
          this.close();
          return;
        }

        const minus = e.target.closest('[data-bk-qty-minus]');
        const plus = e.target.closest('[data-bk-qty-plus]');
        const remove = e.target.closest('[data-bk-remove]');
        const recAdd = e.target.closest('[data-bk-rec-add]');

        if (minus || plus || remove) {
          const item = e.target.closest('.bk-smart-cart__item');
          if (!item) return;
          const key = item.dataset.key;
          const currentQty = Number(item.dataset.quantity || 0);

          if (remove) this.changeLine(key, 0);
          if (minus) this.changeLine(key, Math.max(0, currentQty - 1));
          if (plus) this.changeLine(key, currentQty + 1);
        }

        if (recAdd) this.addRecommendation(recAdd);
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.root.classList.contains('is-open')) this.close();
      });

      document.addEventListener('click', (e) => {
        if (!this.interceptCartLinks) return;
        if (e.target.closest('[data-bk-ignore-cart]')) return;

        const explicit = e.target.closest('[data-bk-cart-open]');
        const cartLink = e.target.closest('a[href="/cart"], a[href$="/cart"], [data-cart-drawer-open], .js_cart, .t4s-site-nav__cart');
        if (explicit || cartLink) {
          e.preventDefault();
          this.open();
        }
      }, true);

      // Fallback for standard HTML add-to-cart forms.
      // Kalles often adds through Ajax, which is monitored separately below.
      if (this.openAfterAdd) {
        document.addEventListener('submit', (e) => {
          const form = e.target;
          if (!(form instanceof HTMLFormElement)) return;
          const action = form.getAttribute('action') || '';
          if (action.includes('/cart/add') && (Date.now() - this.lastNetworkCartAction) > 800) {
            this.scheduleRefresh(true, 900);
          }
        }, true);
      }

      document.addEventListener('shopify:section:load', () => {
        this.refresh(false);
      });
    }

    patchKallesFriendlyHooks() {
      const self = this;

      window.BowkartCart = {
        open: () => self.open(),
        close: () => self.close(),
        refresh: () => self.refresh(false)
      };

      ['cart:updated', 'cart:refresh', 'cart:update', 't4s:cart:updated'].forEach(name => {
        document.addEventListener(name, () => this.refresh(false));
      });
    }


    scheduleRefresh(openDrawer = false, delay = 80) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(async () => {
        if (openDrawer) {
          this.lastFocused = document.activeElement;
          this.root.classList.add('is-open');
          this.panel.setAttribute('aria-hidden', 'false');
          document.documentElement.classList.add('bk-cart-locked');
        }
        await this.refresh(true);
      }, delay);
    }

    installCartNetworkObserver() {
      if (window.__bkSmartCartNetworkObserverInstalled) return;
      window.__bkSmartCartNetworkObserverInstalled = true;

      const instances = () =>
        Array.from(document.querySelectorAll('[data-bk-smart-cart]'))
          .map(el => el.__bowkartSmartCart)
          .filter(Boolean);

      const notify = (url, ok = true) => {
        if (!ok || !url) return;
        const u = String(url);
        const isAdd = /\/cart\/add(?:\.js)?(?:\?|$)/.test(u);
        const isChange = /\/cart\/change(?:\.js)?(?:\?|$)/.test(u);
        const isUpdate = /\/cart\/update(?:\.js)?(?:\?|$)/.test(u);
        const isClear = /\/cart\/clear(?:\.js)?(?:\?|$)/.test(u);

        if (!(isAdd || isChange || isUpdate || isClear)) return;

        instances().forEach(instance => {
          instance.lastNetworkCartAction = Date.now();
          // Important for Kalles quick-add: refresh only AFTER the Ajax request completes.
          instance.scheduleRefresh(isAdd && instance.openAfterAdd, 120);
        });
      };

      // Observe fetch-based cart requests.
      if (window.fetch && !window.fetch.__bkWrapped) {
        const nativeFetch = window.fetch.bind(window);

        const wrappedFetch = async (...args) => {
          const requestUrl =
            typeof args[0] === 'string'
              ? args[0]
              : (args[0] && args[0].url) || '';

          const response = await nativeFetch(...args);
          try { notify(requestUrl, response.ok); } catch (_) {}
          return response;
        };

        wrappedFetch.__bkWrapped = true;
        window.fetch = wrappedFetch;
      }

      // Observe XMLHttpRequest-based Kalles quick-add requests.
      if (window.XMLHttpRequest && !window.XMLHttpRequest.prototype.__bkWrapped) {
        const proto = window.XMLHttpRequest.prototype;
        const nativeOpen = proto.open;
        const nativeSend = proto.send;

        proto.open = function(method, url, ...rest) {
          this.__bkRequestUrl = url;
          return nativeOpen.call(this, method, url, ...rest);
        };

        proto.send = function(...args) {
          this.addEventListener('loadend', () => {
            try {
              notify(this.__bkRequestUrl, this.status >= 200 && this.status < 400);
            } catch (_) {}
          }, { once: true });

          return nativeSend.apply(this, args);
        };

        proto.__bkWrapped = true;
      }
    }

    async open(forceRefresh = false) {
      this.lastFocused = document.activeElement;
      this.root.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('bk-cart-locked');
      await this.refresh(forceRefresh);
      requestAnimationFrame(() => this.root.querySelector('[data-bk-cart-close]')?.focus());
    }

    close() {
      this.root.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('bk-cart-locked');
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }

    async getCart() {
      const response = await fetch(this.route('/cart.js'), {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('Unable to load cart');
      return response.json();
    }

    async refresh() {
      // Cart refreshes are intentionally allowed to be re-scheduled by Kalles Ajax completion.
      if (this.busy) {
        this.scheduleRefresh(false, 180);
        return;
      }
      this.busy = true;
      this.loadingEl.hidden = false;

      try {
        this.cart = await this.getCart();
        this.renderCart();
        await this.loadRecommendations();
        this.dispatchCartState();
      } catch (err) {
        console.error('[Bowkart Smart Cart]', err);
      } finally {
        this.loadingEl.hidden = true;
        this.busy = false;
      }
    }

    renderCart() {
      const cart = this.cart;
      const isEmpty = !cart || cart.item_count === 0;

      this.emptyEl.hidden = !isEmpty;
      this.footerEl.hidden = isEmpty;
      this.itemsEl.hidden = isEmpty;
      this.countTextEl.textContent = isEmpty ? '' : `(${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'})`;

      this.itemsEl.innerHTML = '';

      if (isEmpty) {
        this.crossSellEl.hidden = true;
        this.updateShipping(0);
        return;
      }

      cart.items.forEach(item => {
        const node = this.itemTemplate.content.firstElementChild.cloneNode(true);
        node.dataset.key = item.key;
        node.dataset.quantity = item.quantity;

        const url = item.url || '#';
        const image = item.featured_image?.url || item.image || '';
        const title = item.product_title || item.title || '';
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';

        node.querySelector('[data-bk-item-url]').href = url;
        node.querySelector('[data-bk-item-title]').href = url;
        node.querySelector('[data-bk-item-title]').textContent = title;

        const imageEl = node.querySelector('[data-bk-item-image]');
        if (image) imageEl.src = image;
        imageEl.alt = title;

        const variantEl = node.querySelector('[data-bk-item-variant]');
        variantEl.textContent = variant;
        variantEl.hidden = !variant;

        node.querySelector('[data-bk-item-price]').textContent = this.money(item.final_line_price);

        const compareEl = node.querySelector('[data-bk-item-compare]');
        if (item.original_line_price > item.final_line_price) {
          compareEl.hidden = false;
          compareEl.textContent = this.money(item.original_line_price);
        }

        node.querySelector('[data-bk-qty-value]').textContent = item.quantity;

        this.itemsEl.appendChild(node);
      });

      this.subtotalEl.textContent = this.money(cart.total_price);
      this.checkoutTotalEl.textContent = this.money(cart.total_price);

      const savings = Math.max(0, cart.original_total_price - cart.total_price);
      this.savingsRow.hidden = savings <= 0;
      if (savings > 0) this.savingsEl.textContent = this.money(savings);

      this.updateShipping(cart.total_price);
    }

    updateShipping(total) {
      if (!this.shippingMessageEl || !this.threshold) return;

      const remaining = Math.max(0, this.threshold - total);
      const progress = Math.min(100, Math.max(0, total / this.threshold * 100));

      if (remaining > 0) {
        const pattern = this.config.shippingRemaining || `You're {amount} away from FREE shipping!`;
        this.shippingMessageEl.textContent = pattern.replace('{amount}', this.money(remaining));
      } else {
        this.shippingMessageEl.textContent = this.config.shippingUnlocked || `You've unlocked FREE shipping ♡`;
      }

      if (this.progressEl) this.progressEl.style.width = progress + '%';
      if (this.shippingValueEl) this.shippingValueEl.textContent = `${this.money(total)} / ${this.money(this.threshold)}`;
    }

    async changeLine(key, quantity) {
      if (this.busy) return;
      this.busy = true;

      try {
        const response = await fetch(this.route('/cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });

        if (!response.ok) throw new Error('Cart update failed');

        this.cart = await response.json();
        this.renderCart();
        await this.loadRecommendations();
        this.dispatchCartState();
      } catch (err) {
        console.error('[Bowkart Smart Cart]', err);
      } finally {
        this.busy = false;
      }
    }

    async loadRecommendations() {
      this.crossSellEl.hidden = true;
      this.recsEl.innerHTML = '';

      if (!this.cart?.items?.length) return;

      let products = [];

      // 1) Bowkart AI recommendation service.
      if (this.recSource === 'ai' && this.aiEndpoint) {
        try {
          products = await this.loadAIRecommendations();
        } catch (err) {
          console.warn('[Bowkart Smart Cart] AI recommendations unavailable; using Shopify fallback.', err);
        }
      }

      // 2) Fallback to Shopify-generated recommendations.
      if (!products.length) {
        const fallbackIntent =
          this.recSource === 'shopify_complementary'
            ? 'complementary'
            : 'related';

        products = await this.loadShopifyRecommendations(fallbackIntent);
      }

      const inCartIds = new Set(this.cart.items.map(i => Number(i.product_id)));
      products = products
        .filter(p => p && !inCartIds.has(Number(p.id)))
        .slice(0, this.recLimit);

      if (!products.length) return;

      products.forEach(product => this.renderRecommendation(product));
      this.crossSellEl.hidden = this.recsEl.children.length === 0;
    }

    async loadShopifyRecommendations(intent = 'related') {
      const anchor = this.cart.items[0];
      const productId = anchor?.product_id;
      if (!productId) return [];

      const endpoint =
        `${this.route('/recommendations/products.json')}` +
        `?product_id=${encodeURIComponent(productId)}` +
        `&limit=${Math.max(this.recLimit + 2, 5)}` +
        `&intent=${encodeURIComponent(intent)}`;

      const response = await fetch(endpoint, {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) return [];

      const data = await response.json();
      return Array.isArray(data.products) ? data.products : [];
    }

    async loadAIRecommendations() {
      const payload = {
        cart: {
          item_count: this.cart.item_count,
          total_price: this.cart.total_price,
          currency: this.currency,
          items: this.cart.items.map(item => ({
            product_id: item.product_id,
            variant_id: item.variant_id,
            handle: item.handle,
            title: item.product_title || item.title,
            variant_title: item.variant_title || '',
            product_type: item.product_type || '',
            vendor: item.vendor || '',
            quantity: item.quantity,
            price: item.final_price,
            line_price: item.final_line_price,
            properties: item.properties || {},
            selling_plan_allocation: item.selling_plan_allocation || null
          }))
        },
        limit: this.recLimit
      };

      const response = await fetch(this.aiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error(`AI endpoint returned ${response.status}`);

      const data = await response.json();
      const suggestions = Array.isArray(data.products) ? data.products : [];

      // AI service may return full product objects OR only Shopify handles.
      const hydrated = [];
      for (const suggestion of suggestions.slice(0, this.recLimit + 2)) {
        if (suggestion?.variants && suggestion?.title) {
          hydrated.push(suggestion);
          continue;
        }

        const handle = suggestion?.handle;
        if (!handle) continue;

        try {
          const productResponse = await fetch(
            `${this.route('/products/')}${encodeURIComponent(handle)}.js`,
            { headers: { 'Accept': 'application/json' } }
          );

          if (!productResponse.ok) continue;

          const product = await productResponse.json();
          product.ai_reason = suggestion.reason || '';
          hydrated.push(product);
        } catch (_) {}
      }

      return hydrated;
    }

    renderRecommendation(product) {
      const node = this.recTemplate.content.firstElementChild.cloneNode(true);

      const img =
        product.featured_image?.url ||
        product.featured_image ||
        product.images?.[0] ||
        '';

      const handleUrl =
        product.handle
          ? `${this.route('/products/')}${product.handle}`
          : '#';

      const url = product.url || handleUrl;

      const imageEl = node.querySelector('[data-bk-rec-image]');
      if (img) imageEl.src = typeof img === 'string' ? img : (img.src || '');
      imageEl.alt = product.title || '';

      node.querySelector('[data-bk-rec-url]').href = url;
      node.querySelector('[data-bk-rec-title]').href = url;
      node.querySelector('[data-bk-rec-title]').textContent = product.title || '';

      const aiBox = node.querySelector('[data-bk-rec-ai]');
      const aiReason = node.querySelector('[data-bk-rec-reason]');
      if (product.ai_reason && aiBox && aiReason) {
        aiBox.hidden = false;
        aiReason.textContent = product.ai_reason;
      }

      const variants = (product.variants || []).filter(v => v.available !== false);
      const select = node.querySelector('[data-bk-rec-variant]');
      const addButton = node.querySelector('[data-bk-rec-add]');

      select.innerHTML = '';

      variants.forEach(variant => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent =
          variant.title === 'Default Title'
            ? 'One Size'
            : variant.title;

        option.dataset.price = variant.price;
        option.dataset.compareAtPrice = variant.compare_at_price || '';
        select.appendChild(option);
      });

      const first = variants[0];

      if (!first) {
        addButton.disabled = true;
        addButton.textContent = 'Sold out';
      } else {
        this.paintRecPrice(node, first);
      }

      if (variants.length <= 1) select.hidden = true;

      select.addEventListener('change', () => {
        const selected = variants.find(
          v => String(v.id) === String(select.value)
        );
        if (selected) this.paintRecPrice(node, selected);
      });

      this.recsEl.appendChild(node);
    }

    paintRecPrice(node, variant) {
      const priceEl = node.querySelector('[data-bk-rec-price]');
      const compareEl = node.querySelector('[data-bk-rec-compare]');

      const price = Number(variant.price || 0);
      const compare = Number(variant.compare_at_price || 0);

      priceEl.textContent = this.money(price);
      compareEl.hidden = !(compare > price);
      if (compare > price) compareEl.textContent = this.money(compare);
    }

    async addRecommendation(button) {
      if (button.disabled) return;
      const card = button.closest('.bk-smart-cart__rec');
      const select = card?.querySelector('[data-bk-rec-variant]');
      const id = select?.value;
      if (!id) return;

      const original = button.innerHTML;
      button.disabled = true;
      button.textContent = 'Adding…';

      try {
        const response = await fetch(this.route('/cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
        });

        if (!response.ok) throw new Error('Add failed');

        button.textContent = this.config.addedText || 'Added ✓';
        setTimeout(() => {
          button.innerHTML = original;
          button.disabled = false;
        }, 900);

        this.cart = await this.getCart();
        this.renderCart();
        await this.loadRecommendations();
        this.dispatchCartState();
      } catch (err) {
        button.textContent = this.config.errorText || 'Try again';
        setTimeout(() => {
          button.innerHTML = original;
          button.disabled = false;
        }, 1300);
      }
    }

    dispatchCartState() {
      document.dispatchEvent(new CustomEvent('bowkart:cart:updated', { detail: { cart: this.cart } }));

      // Common Kalles/cart counters: update when present without depending on a specific theme version.
      const count = this.cart?.item_count || 0;
      document.querySelectorAll('[data-cart-count], .cart-count, .t4s-cart-count').forEach(el => {
        el.textContent = count;
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-bk-smart-cart]').forEach(root => {
      if (!root.__bowkartSmartCart) {
        root.__bowkartSmartCart = new BowkartSmartCart(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
